import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { useT, LANG_OPTIONS, LANG_STORAGE_KEY } from './lib/i18n';
import Plotted from './Plotted';
import ShareView from './ShareView';
import MapScreen from './MapScreen';

const C = {
  bg:         "#07101a",
  card:       "#0d1a27",
  border:     "#1e3347",
  accent:     "#009EC2",
  accentHi:   "#4DD4F0",
  accentGlow: "rgba(0,158,194,0.30)",
  text:       "#E6FBFF",
  sub:        "#7ab8c8",
  muted:      "#3d6b7a",
  green:      "#27C97A",
  rose:       "#F06292",
  white:      "#ffffff",
};

const BG_PHOTOS = [
  "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1200&q=80",
  "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1200&q=80",
  "https://images.unsplash.com/photo-1485871981521-5b1fd3805eee?w=1200&q=80",
  "https://images.unsplash.com/photo-1541849546-216549ae216d?w=1200&q=80",
  "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1200&q=80",
  "https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=1200&q=80",
  "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=1200&q=80",
  "https://images.unsplash.com/photo-1508193638397-1c4234db14d8?w=1200&q=80",
];

// ── Icons ─────────────────────────────────────────────────────────────────────

const IconEmail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const IconLock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const IconEyeOff = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const IconEye = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

// ── Capture ref code from URL ──────────────────────────────────────────────────
const params = new URLSearchParams(window.location.search);
const pendingRef = params.get('ref');
if (pendingRef) sessionStorage.setItem('plotted_pending_ref', pendingRef);

// ── Splash screen ─────────────────────────────────────────────────────────────

const _SPLASH_PHOTOS = BG_PHOTOS;
const _splashPhoto   = _SPLASH_PHOTOS[Math.floor(Math.random() * _SPLASH_PHOTOS.length)];

function SplashScreen({ onDone }) {
  const storedLang = localStorage.getItem(LANG_STORAGE_KEY);
  const deviceLang = (navigator.language || 'fr').split('-')[0].toLowerCase();
  const lang       = LANG_OPTIONS.find(l => l.code === (storedLang || deviceLang))?.code || 'fr';
  const t          = useT(lang);

  const firstName  = (() => {
    try { return JSON.parse(localStorage.getItem('travel_prefs') || '{}').firstName || ''; }
    catch { return ''; }
  })();

  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    // Fade in
    const t1 = setTimeout(() => setOpacity(1), 30);
    // Fade out start
    const t2 = setTimeout(() => setOpacity(0), 4600);
    // Unmount
    const t3 = setTimeout(() => onDone(), 5100);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: C.bg,
      opacity,
      transition: 'opacity 0.45s ease',
    }}>
      {/* Background photo */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url(${_splashPhoto})`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        opacity: 0.22,
      }}/>
      {/* Gradient overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(to bottom, transparent 0%, ${C.bg}bb 50%, ${C.bg} 75%)`,
      }}/>

      {/* Tagline — centered at 40% from top */}
      <div style={{
        position: 'absolute', left: 0, right: 0,
        top: '40%', transform: 'translateY(-50%)',
        textAlign: 'center', padding: '0 40px',
      }}>
        <div style={{ fontSize: 20, color: C.text, fontStyle: 'italic', fontWeight: 300, lineHeight: 1.65 }}>
          {t('splash.tagline1')}
        </div>
        <div style={{ fontSize: 20, color: C.text, fontStyle: 'italic', fontWeight: 300, lineHeight: 1.65, marginTop: 2 }}>
          {t('splash.tagline2')}
        </div>
      </div>

      {/* Logo — bottom edge at 20% from bottom */}
      <div style={{
        position: 'absolute', left: 0, right: 0,
        bottom: '10%',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 38, fontWeight: 900, letterSpacing: '-2px', color: C.text }}>
          plotted<span style={{ color: C.accentHi }}>.</span>
        </div>
      </div>

{/* Welcome — midway between tagline (40%) and logo (~80%) → 60% from top */}
      {firstName ? (
        <div style={{
          position: 'absolute', left: 0, right: 0,
          top: '65%', transform: 'translateY(-50%)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 15, color: C.accentHi, fontWeight: 500 }}>
            {t('splash.welcome')}&nbsp;{firstName}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── App shell ─────────────────────────────────────────────────────────────────

function AppShell() {
  return (
    <AuthProvider>
      <AppShellInner />
    </AuthProvider>
  );
}

function AppShellInner() {
  const { session, loading, signOut } = useAuth();
  const [splashDone, setSplashDone] = useState(false);
  const [sharedToken]           = useState(() => new URLSearchParams(window.location.search).get('shared'));

  if (loading) return (
    <div style={{ background: C.bg, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.text, fontSize: 28, fontWeight: 800, letterSpacing: '-1px' }}>
        plotted<span style={{ color: C.accentHi }}>.</span>
      </div>
    </div>
  );

  if (sharedToken) return <VoyeurMode token={sharedToken} loggedIn={!!session} />;
  if (!session) return <Auth />;

  if (!splashDone) return <SplashScreen onDone={() => setSplashDone(true)} />;
  return <Plotted user={session.user} onSignOut={signOut} />;
}

// ── Voyeur mode ───────────────────────────────────────────────────────────────

function VoyeurMode({ token, loggedIn = false }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signupGate, setGate] = useState(false);

  useEffect(() => {
    supabase.from('shared_topn').select('payload').eq('token', token).single()
      .then(({ data }) => { setPayload(data?.payload || null); setLoading(false); });
  }, [token]);

  const goSignup = () => { window.location.href = window.location.origin; };
  const ctaLabel = loggedIn ? 'Aller sur mon Plotted.' : 'Créer mon compte →';

  if (loading) return (
    <div style={{ background: C.bg, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.text, fontSize: 28, fontWeight: 800, letterSpacing: '-1px' }}>
        plotted<span style={{ color: C.accentHi }}>.</span>
      </div>
    </div>
  );

  if (!payload) return (
    <div style={{ background: C.bg, height: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24 }}>
      <div style={{ fontSize: 40 }}>🔗</div>
      <div style={{ color: C.text, fontSize: 18, fontWeight: 700 }}>Lien invalide</div>
      <button onClick={goSignup} style={voyeurCtaBtn}>{ctaLabel}</button>
    </div>
  );

  const dests     = payload.dests || [];
  const ownerName = payload.ownerName || '';
  const planCount = payload.planCount || dests.filter(d => d.inPlan).length;
  const selIds     = dests.map(d => d.id).filter(Boolean);
  const planPhotos = Object.fromEntries(
    dests.filter(d => d.inPlan && d.id && d.photoUrl).map(d => [d.id, d.photoUrl])
  );

  const TABS = [
    { id: 'map',  icon: '🗺️', label: 'Destinations' },
    { id: 'cal',  icon: '📅', label: 'Mes slots' },
    { id: 'home', icon: '✈️', label: 'Top N' },
    { id: 'prof', icon: '👤', label: 'Profil' },
  ];

  return (
    <div style={{ background: C.bg, width: '100%', height: '100vh', display: 'flex', flexDirection: 'column',
      fontFamily: "'DM Sans',system-ui,sans-serif", position: 'relative', overflow: 'hidden' }}>

      {/* Map — fills all space between nav (inside MapScreen) and tab bar */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <MapScreen
          selIds={selIds}
          setSelIds={() => {}}
          appLang={payload.lang || 'fr'}
          readOnly
          onSignup={() => setGate(true)}
          voyeurTitle={ownerName ? `Bucket list de ${ownerName}` : 'Bucket list'}
          planPhotos={planPhotos}
        />
      </div>

      {/* Tab bar */}
      <div style={{ background: C.card, borderTop: `1px solid ${C.border}`,
        display: 'flex', justifyContent: 'space-around', alignItems: 'stretch',
        paddingBottom: 'env(safe-area-inset-bottom,0px)', flexShrink: 0 }}>
        {TABS.map(tab => {
          const isActive = tab.id === 'map';
          return (
            <button key={tab.id}
              onClick={() => !isActive && setGate(true)}
              style={{ flex: 1, padding: '10px 4px 8px', background: 'none', border: 'none',
                cursor: isActive ? 'default' : 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                opacity: isActive ? 1 : 0.45 }}>
              <span style={{ fontSize: 20 }}>{tab.icon}</span>
              <span style={{ fontSize: 9, fontWeight: 700, color: isActive ? C.accentHi : C.muted,
                letterSpacing: 0.3 }}>
                {isActive ? 'Mes destinations' : tab.label}
              </span>
              {!isActive && <span style={{ fontSize: 7, color: C.muted }}>🔒</span>}
            </button>
          );
        })}
      </div>

      {/* Signup gate modal */}
      {signupGate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,16,26,0.85)',
          backdropFilter: 'blur(12px)', zIndex: 3000,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={() => setGate(false)}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '24px 24px 0 0', padding: '28px 24px 40px',
            maxWidth: 480, width: '100%', textAlign: 'center' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>{loggedIn ? '🗺️' : '✨'}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: C.text, marginBottom: 8 }}>
              {loggedIn ? 'Tu as déjà un compte !' : 'Crée ta propre carte'}
            </div>
            <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.7, marginBottom: 24 }}>
              {loggedIn
                ? 'Retourne voir tes recommandations personnalisées.'
                : ownerName
                  ? `${ownerName} a ses ${planCount} destinations favorites. Et toi ?`
                  : 'Crée ta bucket list, calcule tes Top N destinations et partage ta carte.'}
            </div>
            <button onClick={goSignup} style={{ ...voyeurCtaBtn, width: '100%', marginBottom: 12 }}>
              {loggedIn ? 'Aller sur mon Plotted.' : 'Créer mon compte gratuitement →'}
            </button>
            <div onClick={() => setGate(false)}
              style={{ fontSize: 13, color: C.muted, cursor: 'pointer' }}>
              Continuer à explorer
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const voyeurCtaBtn = {
  background: 'linear-gradient(135deg,#006F87,#009EC2)',
  border: 'none', borderRadius: 50, padding: '14px 32px',
  color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer',
  boxShadow: '0 8px 32px rgba(0,158,194,0.35)',
  display: 'inline-block',
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/share/:token" element={<ShareView />} />
        <Route path="/*" element={<AppShell />} />
      </Routes>
    </BrowserRouter>
  );
}

// ── Auth ──────────────────────────────────────────────────────────────────────

function Auth() {
  const deviceLang = (navigator.language || 'fr').split('-')[0].toLowerCase();
  const storedLang = localStorage.getItem(LANG_STORAGE_KEY);
  const initLang   = LANG_OPTIONS.find(l => l.code === (storedLang || deviceLang))?.code || 'fr';

  const [selectedLang, setSelectedLang] = useState(initLang);
  const [langOpen, setLangOpen]         = useState(false);
  const t = useT(selectedLang);
  const currentLang = LANG_OPTIONS.find(l => l.code === selectedLang) || LANG_OPTIONS[0];

  const [isLogin, setIsLogin]       = useState(true);
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [showPwd, setShowPwd]       = useState(false);
  const [focused, setFocused]       = useState('');
  const [error, setError]           = useState('');
  const [message, setMessage]       = useState('');
  const [loading, setLoading]       = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [bgIndex, setBgIndex]       = useState(() => Math.floor(Math.random() * BG_PHOTOS.length));

  useEffect(() => {
    const interval = setInterval(() => setBgIndex(i => (i + 1) % BG_PHOTOS.length), 5000);
    return () => clearInterval(interval);
  }, []);

  const pickLang = (code) => {
    setSelectedLang(code);
    localStorage.setItem(LANG_STORAGE_KEY, code);
    setLangOpen(false);
  };

  const reset = () => { setError(''); setMessage(''); };
  const switchMode = () => { reset(); setIsLogin(v => !v); setForgotMode(false); };

  const handleSubmit = async () => {
    reset();
    setLoading(true);
    if (forgotMode) {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      setLoading(false);
      if (error) setError(error.message);
      else setMessage(t('auth.resetSent') + email);
      return;
    }
    if (isLogin) {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      setLoading(false);
      if (error) setError(error.message);
    } else {
      const { data, error } = await supabase.auth.signUp({ email, password });
      setLoading(false);
      if (error) { setError(error.message); return; }
      // Referral hook — await so plan is set before profile loads
      const ref = sessionStorage.getItem('plotted_pending_ref');
      if (ref && data?.user?.id) {
        const { data: refData } = await supabase.functions.invoke('referral-signup', { body: { ref_code: ref, new_user_id: data.user.id } });
        sessionStorage.removeItem('plotted_pending_ref');
        if (refData?.ok) sessionStorage.setItem('plotted_referral_plan', 'silver');
      }
      setMessage(t('auth.checkEmail'));
    }
  };

  const [magicMode, setMagicMode] = useState(false);
  const handleMagicLink = async () => {
    reset(); setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    setLoading(false);
    if (error) setError(error.message);
    else setMessage(t('auth.magicSent'));
  };

  const handleGoogle = async () => {
    const ref = sessionStorage.getItem('plotted_pending_ref');
    const redirectTo = ref
      ? `${window.location.origin}?ref=${ref}`
      : window.location.origin;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
    if (error) setError(error.message);
  };

  const inputWrap = () => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  });

  const inputStyle = (name) => ({
    width: '100%',
    padding: '14px 16px 14px 44px',
    borderRadius: '14px',
    border: `1.5px solid ${focused === name ? C.accent : 'rgba(255,255,255,0.15)'}`,
    background: focused === name ? 'rgba(0,158,194,0.08)' : 'rgba(7,16,26,0.7)',
    color: C.text,
    fontSize: '15px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.2s',
    fontFamily: "-apple-system,'SF Pro Display',sans-serif",
    backdropFilter: 'blur(10px)',
  });

  const iconStyle = {
    position: 'absolute',
    left: 14,
    color: focused ? C.accentHi : 'rgba(255,255,255,0.4)',
    pointerEvents: 'none',
    display: 'flex',
  };

  const title = forgotMode
    ? t('auth.forgotTitle')
    : isLogin ? t('auth.loginTitle') : t('auth.signupTitle');

  const subtitle = forgotMode
    ? t('auth.forgotSub')
    : isLogin ? t('auth.loginSub') : t('auth.signupSub');

  const submitLabel = loading ? '…' : forgotMode
    ? t('auth.sendLink')
    : isLogin ? t('auth.signIn') : t('auth.signUp');

  return (
    <div style={{
      position: 'fixed', inset: 0,
      fontFamily: "-apple-system,'SF Pro Display','Helvetica Neue',sans-serif",
    }}
      onClick={() => langOpen && setLangOpen(false)}
    >
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        {BG_PHOTOS.map((photo, i) => (
          <div key={i} style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${photo})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.65)',
            opacity: i === bgIndex ? 1 : 0,
            transition: 'opacity 1.5s ease-in-out',
          }}/>
        ))}
      </div>

      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to bottom, rgba(7,16,26,0.5) 0%, transparent 30%, transparent 40%, rgba(7,16,26,0.88) 65%, rgba(7,16,26,1) 100%)',
      }}/>

      <div style={{
        position: 'absolute', top: '9%', left: 0, right: 0,
        textAlign: 'center', zIndex: 2,
      }}>
        <div style={{
          fontSize: 'clamp(42px, 10vw, 64px)', fontWeight: 800,
          color: C.white, letterSpacing: '-2px', lineHeight: 1,
          textShadow: '0 2px 20px rgba(0,0,0,0.5)',
        }}>
          plotted<span style={{ color: C.accentHi }}>.</span>
        </div>
        {/* Brand signature. Replaces the hardcoded English line that sat here
            ("Your next adventure, already plotted."), keeping its exact styling
            so it stays readable over the photo background. */}
        <div style={{
          color: 'rgba(255,255,255,0.65)', fontSize: 14, marginTop: 8,
          fontWeight: 400, letterSpacing: '0.3px',
        }}>
          {t('brand.tagline')}
        </div>
      </div>

      <div style={{
        position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 480, zIndex: 2,
        padding: '0 44px 20px', boxSizing: 'border-box',
      }}>
        {(forgotMode || !isLogin) && (
          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.white, marginBottom: 4 }}>{title}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{subtitle}</div>
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(240,98,146,0.15)', border: '1px solid rgba(240,98,146,0.4)',
            borderRadius: 12, padding: '10px 14px', color: '#ff8fab',
            fontSize: 13, marginBottom: 12, backdropFilter: 'blur(10px)',
          }}>{error}</div>
        )}
        {message && (
          <div style={{
            background: 'rgba(39,201,122,0.15)', border: '1px solid rgba(39,201,122,0.4)',
            borderRadius: 12, padding: '10px 14px', color: C.green,
            fontSize: 13, marginBottom: 12, backdropFilter: 'blur(10px)',
          }}>{message}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
          <div style={inputWrap()}>
            <span style={iconStyle}><IconEmail /></span>
            <input
              type="email" placeholder="Email"
              value={email} onChange={e => setEmail(e.target.value)}
              onFocus={() => setFocused('email')} onBlur={() => setFocused('')}
              style={inputStyle('email')}
            />
          </div>

          {!forgotMode && (
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={iconStyle}><IconLock /></span>
              <input
                type={showPwd ? 'text' : 'password'} placeholder={t('auth.phPassword')}
                value={password} onChange={e => setPassword(e.target.value)}
                onFocus={() => setFocused('password')} onBlur={() => setFocused('')}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                style={{ ...inputStyle('password'), paddingRight: 44 }}
              />
              <button onClick={() => setShowPwd(v => !v)} style={{
                position: 'absolute', right: 14, background: 'none', border: 'none',
                cursor: 'pointer', color: 'rgba(255,255,255,0.4)', display: 'flex', padding: 4,
              }}>
                {showPwd ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
          )}

          {isLogin && !forgotMode && (
            <div style={{ textAlign: 'right' }}>
              <span onClick={() => { reset(); setForgotMode(true); }} style={{
                fontSize: 13, color: C.accentHi, cursor: 'pointer', fontWeight: 500,
              }}>{t('auth.forgotLink')}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            {!forgotMode && (
              <div style={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                <button onClick={() => setLangOpen(o => !o)} style={{
                  height: '100%', minHeight: 52, padding: '0 14px',
                  borderRadius: 50,
                  background: langOpen ? 'rgba(0,158,194,0.2)' : 'rgba(255,255,255,0.12)',
                  border: `1.5px solid ${langOpen ? C.accent : 'rgba(255,255,255,0.5)'}`,
                  cursor: 'pointer', backdropFilter: 'blur(10px)',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <span style={{ fontSize: 20, lineHeight: 1 }}>{currentLang.flag}</span>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>▾</span>
                </button>

                {langOpen && (
                  <div style={{
                    position: 'absolute', bottom: 'calc(100% + 8px)', left: 0,
                    background: 'rgba(7,16,26,0.97)', backdropFilter: 'blur(20px)',
                    border: `1px solid ${C.border}`, borderRadius: 16, padding: 6,
                    display: 'flex', flexDirection: 'column', gap: 2,
                    zIndex: 100, boxShadow: '0 -8px 32px rgba(0,0,0,0.6)',
                  }}>
                    {LANG_OPTIONS.map(l => (
                      <button key={l.code} onClick={() => pickLang(l.code)} style={{
                        background: selectedLang === l.code ? 'rgba(0,158,194,0.18)' : 'none',
                        border: `1px solid ${selectedLang === l.code ? C.accent : 'transparent'}`,
                        borderRadius: 10, padding: '7px 14px',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                        color: selectedLang === l.code ? C.accentHi : C.text,
                        fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', lineHeight: 1,
                      }}>
                        <span style={{ fontSize: 19, lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}>{l.flag}</span>
                        <span style={{ lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}>{l.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button onClick={handleSubmit} disabled={loading} style={{
              flex: 1, padding: '16px',
              background: `linear-gradient(135deg,#006F87,${C.accent})`,
              color: C.white, border: 'none', borderRadius: 50,
              fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: `0 8px 24px ${C.accentGlow}`,
              opacity: loading ? 0.7 : 1,
              transition: 'opacity 0.2s',
            }}>
              {submitLabel}
            </button>
          </div>

          {forgotMode && (
            <div onClick={() => { reset(); setForgotMode(false); }} style={{
              textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer', padding: '4px 0',
            }}>
              {t('auth.backToLogin')}
            </div>
          )}
        </div>

        {!forgotMode && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.15)' }}/>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>{t('auth.orWith')}</span>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.15)' }}/>
            </div>

            <button onClick={handleGoogle} style={{
              width: '100%', padding: '15px',
              background: 'rgba(255,255,255,0.95)', color: '#1a1a1a',
              border: 'none', borderRadius: 50,
              fontSize: 15, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              marginBottom: 16,
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            }}>
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              {t('auth.googleBtn')}
            </button>

            <button onClick={handleMagicLink} disabled={!email || loading} style={{
              width: '100%', padding: '13px',
              background: 'none', color: email ? C.accentHi : 'rgba(255,255,255,0.3)',
              border: `1.5px solid ${email ? 'rgba(0,158,194,0.4)' : 'rgba(255,255,255,0.12)'}`,
              borderRadius: 50, fontSize: 14, fontWeight: 600,
              cursor: email ? 'pointer' : 'default', marginBottom: 16,
            }}>
              {t('auth.magicBtn')}
            </button>

            <div style={{ textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>
              {isLogin
                ? <>{t('auth.noAccount')}{' '}<span onClick={switchMode} style={{ color: C.accentHi, fontWeight: 600, cursor: 'pointer' }}>{t('auth.signUp')}</span></>
                : <>{t('auth.haveAccount')}{' '}<span onClick={switchMode} style={{ color: C.accentHi, fontWeight: 600, cursor: 'pointer' }}>{t('auth.signIn')}</span></>
              }
            </div>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 8, color: 'rgba(255,255,255,0.18)', fontSize: 10, lineHeight: 1.3 }}>
          {t('auth.terms')}
        </div>
      </div>
    </div>
  );
}

export default App;
